import { Check, Copy } from 'lucide-react';
import { useState, useEffect } from 'react';
import { AgentList, CreateAgentModal } from './agents';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { useClipboard } from '../hooks/useClipboard';
import { createApiClient } from '../lib/api-client';
import { useApiStore } from '../store/api';
import { Button } from './ui/Button';

interface Agent {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  user_type: 'bot';
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
}

interface NewAgent extends Agent {
  api_key: string;
}

interface AgentsProps {
  onBack: () => void;
}

export default function Agents({ onBack }: AgentsProps) {
  const { apiUrl } = useApiStore();
  const { copy } = useClipboard();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newlyCreatedAgent, setNewlyCreatedAgent] = useState<NewAgent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    loadAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Run once on mount
  }, []);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const client = createApiClient(apiUrl);
      const { data, error } = await client.GET('/v1/agents');

      if (error || !data) {
        throw new Error('Failed to load agents');
      }

      setAgents((data.agents || []) as Agent[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async (name: string, avatarUrl: string) => {
    try {
      setCreating(true);
      setError(null);

      const client = createApiClient(apiUrl);
      const { data, error } = await client.POST('/v1/agents', {
        body: {
          display_name: name,
          avatar_url: avatarUrl || null,
        },
      });

      if (error || !data) {
        throw new Error('Failed to create agent');
      }

      if ('agent' in data && 'api_key' in data) {
        setNewlyCreatedAgent({
          ...(data.agent as Agent),
          api_key: data.api_key as string,
        });
      }
      setShowCreateModal(false);
      await loadAgents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create agent');
      throw err;
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAgent = (id: string) => {
    setConfirmDeleteId(id);
  };

  const confirmDeleteAgent = async () => {
    if (!confirmDeleteId) return;
    try {
      const client = createApiClient(apiUrl);
      const { error } = await client.DELETE('/v1/agents/{agent_id}', {
        params: { path: { agent_id: confirmDeleteId } },
      });

      if (error) {
        throw new Error('Failed to delete agent');
      }

      await loadAgents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete agent');
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const handleCopyApiKey = async (apiKey: string) => {
    try {
      await copy(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="px-3 py-1 text-slate-300 hover:text-white transition-colors min-h-[44px] min-w-[44px]"
                aria-label="Back to dashboard"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-white">Agent Management</h1>
            </div>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {newlyCreatedAgent && (
          <div className="mb-6 bg-green-900/20 border border-green-700 rounded-lg p-6">
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg font-semibold text-green-400">Agent Created Successfully!</h3>
              <button
                onClick={() => setNewlyCreatedAgent(null)}
                className="text-slate-400 hover:text-white min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Dismiss notification"
              >
                ✕
              </button>
            </div>
            <p className="text-slate-300 text-sm mb-4">
              Copy this API key now - you won't be able to see it again!
            </p>
            <div className="bg-slate-900 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3 mb-3">
                {newlyCreatedAgent.avatar_url && (
                  <img
                    src={newlyCreatedAgent.avatar_url}
                    alt={newlyCreatedAgent.display_name || 'Agent'}
                    className="w-12 h-12 rounded-full"
                  />
                )}
                <div>
                  <p className="text-white font-medium">
                    {newlyCreatedAgent.display_name || 'Unnamed Agent'}
                  </p>
                  <p className="text-slate-400 text-sm">ID: {newlyCreatedAgent.id}</p>
                </div>
              </div>
              <div className="font-mono text-sm break-all">
                <p className="text-slate-400 text-xs mb-1">API Key:</p>
                <div className="flex items-center justify-between gap-4 bg-slate-800 rounded p-3">
                  <code className="text-green-400 flex-1" data-testid="agent-api-key-value">
                    {newlyCreatedAgent.api_key}
                  </code>
                  <Button
                    size="sm"
                    onClick={() => handleCopyApiKey(newlyCreatedAgent.api_key)}
                    className="!bg-slate-700 hover:!bg-slate-600 flex-shrink-0 min-w-[80px]"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-900/20 border border-red-700 rounded-lg p-4">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Create New Agent Button */}
        <div className="mb-6">
          <Button
            onClick={() => setShowCreateModal(true)}
            variant="primary"
            className="!bg-blue-600 hover:!bg-blue-700"
          >
            + Create New Agent
          </Button>
        </div>

        {/* Agents List */}
        <AgentList agents={agents} loading={loading} onDelete={handleDeleteAgent} />

        {/* Usage Instructions */}
        {agents.length > 0 && (
          <div className="mt-6 bg-slate-800 border border-slate-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Using Your Agents</h3>
            <div className="space-y-3 text-slate-300 text-sm">
              <p>
                Each agent has its own API key. Use the agent's API key in the Authorization header
                to make requests on behalf of the agent:
              </p>
              <div className="bg-slate-900 rounded p-3 font-mono text-xs overflow-x-auto">
                <code className="text-green-400">
                  curl {apiUrl}/v1/conversations \<br />
                  &nbsp;&nbsp;-H "Authorization: Bearer AGENT_API_KEY"
                </code>
              </div>
              <p className="mt-4">
                <strong>Note:</strong> You can add agents to conversations and give them
                conversation-specific aliases for @mentions.
              </p>
            </div>
          </div>
        )}
      </main>

      {/* Create Agent Modal */}
      <CreateAgentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateAgent}
        creating={creating}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDeleteId !== null}
        title="Delete Agent"
        message="Are you sure you want to delete this agent? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirmDeleteAgent}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}
